/**
 * Cliente REST.
 *
 * El token de sesión vive en localStorage y se adjunta a cada petición. No
 * usamos cookies porque el WebSocket necesita el mismo token y así hay una
 * sola fuente para ambos canales.
 */

const TOKEN_KEY = 'open_dahu_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* modo privado o almacenamiento bloqueado: la sesión dura lo que la pestaña */
  }
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`/api${path}`, { ...init, headers });

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* respuesta sin cuerpo */
  }

  if (!res.ok) {
    // 413 y 504 los devuelve nginx, no la API: llegan en HTML y sin `error`.
    const message =
      (data as { error?: string } | null)?.error ??
      (res.status === 413
        ? 'El archivo es demasiado grande para el servidor. Prueba con un PDF más pequeño.'
        : res.status === 504
          ? 'El servidor tardó demasiado en responder. Prueba con menos preguntas o un documento más corto.'
          : `Error ${res.status} al llamar a ${path}`);
    // La sesión caducada se limpia sola: así el usuario ve la pantalla de
    // entrada en lugar de errores repetidos.
    if (res.status === 401) setToken(null);
    throw new ApiError(message, res.status);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) }),

  /** Sube un PDF como cuerpo binario crudo. */
  postPdf: <T>(path: string, file: File) =>
    request<T>(path, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    }),
};

/* ------------------------------------------------------------------ *
 * Tipos de respuesta
 * ------------------------------------------------------------------ */

/**
 * Estadísticas sumando las partidas jugadas en la propia sala, que las
 * oficiales no cuentan. Para el filtro "Incluir partidas como anfitrión".
 */
export interface StatsWithHost {
  hostPlayedGames: number;
  dRating: number;
  wins: number;
  totalGames: number;
  totalAnswers: number;
  correctAnswers: number;
  accuracy: number;
  avgResponseTimeMs: number;
  bestStreak: number;
}

export interface Profile {
  id: string;
  username: string;
  displayName: string;
  avatar: Record<string, unknown>;
  dRating: number;
  dahus: number;
  wins: number;
  totalGames: number;
  totalAnswers: number;
  correctAnswers: number;
  accuracy: number;
  avgResponseTimeMs: number;
  bestStreak: number;
  accuracyByType: Record<string, { correct: number; total: number }>;
  withHost?: StatsWithHost;
  createdAt: string;
  /** ISO 3166-1 alfa-2. Nulo solo en cuentas que aún no han completado el perfil. */
  country: string | null;
  countryName: string | null;
  /** Años cumplidos. El servidor no envía la fecha de nacimiento, solo la edad. */
  age: number | null;
  /** `false` mientras falten la fecha de nacimiento o el país. */
  profileComplete: boolean;
  /** Aceptó la versión vigente de los Términos y la Política de privacidad. */
  termsAccepted: boolean;
  /** `false` si con las reglas de edad de su país la cuenta ya no puede usarse. */
  ageAllowed: boolean;
  /** Menor de la edad de consentimiento de su país: sin billetera ni perfil público. */
  restrictedMinor: boolean;
  /** `false` en cuentas creadas con Google que aún no han puesto contraseña. */
  hasPassword: boolean;
  email: string | null;
  /**
   * Nombre y apellido reales. Solo llegan a la propia cuenta: ni el ranking ni
   * las salas los incluyen, y no se muestran a otros jugadores.
   */
  firstName: string | null;
  lastName: string | null;
  /** `AIza…9f2K` si hay clave de IA guardada; null si no. Nunca la clave entera. */
  aiKeyPreview: string | null;
  /** Administra la plataforma: ve el panel de administración. */
  isAdmin?: boolean;
  /** 'user' o 'mega_guardian'. */
  role: string;
  /** Puede supervisar partidas (grabar la pantalla de los jugadores). */
  canMonitor?: boolean;
  /** Tema guardado en la cuenta, o null si nunca se eligió uno. */
  theme: 'light' | 'dark' | null;
  aiCalls: number;
  aiLastUsedAt: string | null;
}

export interface WalletInfo {
  address: string;
  linkedAt: string;
}

export interface QuizSummary {
  id: string;
  title: string;
  description: string;
  category: string;
  coverEmoji: string;
  coverColor: string;
  creatorId: string | null;
  creatorName: string;
  questionCount: number;
  playsCount: number;
  visibility: string;
  createdAt: string;
  /** ISO 639-1, declarado por quien creó la trivia. */
  language: string;
  languageName: string | null;
  /** Solo en «Mis trivias». */
  folder?: string;
}

export interface LedgerEntry {
  delta: number;
  reason: string;
  ref: string | null;
  created_at: string;
}

export interface HistoryEntry {
  quiz_id: string;
  quiz_title: string;
  game_mode: string;
  placement: number;
  total_players: number;
  score: number;
  accuracy: number;
  rating_change: number;
  new_rating: number;
  dahus_awarded: number;
  played_at: string;
  /** 1 si abrió la sala. Con `placement` 0, la dirigió sin jugar. */
  was_host: number;
}

/** Lo que cualquiera puede ver de otra cuenta. */
export interface PublicUserProfile {
  id: string;
  username: string;
  avatar: unknown;
  dRating: number;
  wins: number;
  totalGames: number;
  hostedGames: number;
  accuracy: number;
  bestStreak: number;
  withHost?: StatsWithHost;
  countryName: string | null;
  createdAt: string;
  /** Relación con quien mira; null si no hay sesión o es su propio perfil. */
  friendship?: FriendshipStatus | null;
}

export interface UserSearchResult {
  id: string;
  username: string;
  avatar: unknown;
  dRating: number;
  totalGames: number;
}

export interface AiStatus {
  configured: boolean;
  provider: 'gemini' | 'openai' | null;
  model: string | null;
  /** `true` cuando se está usando la clave propia y no una del servidor. */
  usingOwnKey: boolean;
}

/* ---------------- amigos ---------------- */

export type { FriendSummary, FriendRequestSummary } from '../../shared/protocol';

export type FriendshipStatus = 'none' | 'friends' | 'outgoing' | 'incoming';

export interface FriendsData {
  friends: import('../../shared/protocol').FriendSummary[];
  incoming: import('../../shared/protocol').FriendRequestSummary[];
  outgoing: import('../../shared/protocol').FriendRequestSummary[];
}
